import { useState, useEffect } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { shedService, flockService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { Shed } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";

const statusColors: Record<string, "success" | "error" | "warning"> = {
  active: "success",
  inactive: "error",
  maintenance: "warning",
};

/**
 * How full a shed is: a bar, the count, and the percentage.
 *
 * Over capacity is shown as over capacity, not clamped to 100%. A shed holding
 * more birds than it is rated for is a welfare and a production problem, and
 * clamping the bar would hide the one case worth seeing.
 */
function ShedOccupancy({
  birds,
  capacity,
  state,
}: {
  birds: number;
  capacity?: number | null;
  state: "loading" | "ready" | "failed";
}) {
  if (state === "loading") {
    return <span className="text-xs text-gray-400">…</span>;
  }
  if (state === "failed") {
    // NOT "0 birds". An occupancy we could not read is unknown; printing zero
    // says the shed is empty, which for a full shed is the worse error.
    return <span className="text-xs text-warning-600 dark:text-warning-400">Unknown</span>;
  }
  if (!capacity || capacity <= 0) {
    return (
      <span className="text-sm text-gray-600 dark:text-gray-400">
        {birds.toLocaleString()}
        <span className="ml-1 text-xs text-gray-400">birds</span>
      </span>
    );
  }
  const pct = (birds / capacity) * 100;
  const over = pct > 100;
  const bar = over ? "bg-error-500" : pct >= 85 ? "bg-warning-500" : "bg-success-500";
  return (
    <div className="min-w-28">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {birds.toLocaleString()}
        </span>
        <span
          className={`text-xs tabular-nums ${over ? "text-error-600 dark:text-error-400" : "text-gray-500 dark:text-gray-400"}`}
        >
          {pct.toFixed(0)}%
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-full ${bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

export default function ShedsPage() {
  const isOwner = useIsOwner();
  // THREE states, not two. /sheds usually resolves before /flocks, so a
  // two-state component renders "0 of 5,000" for a beat on every shed — and
  // this component's own reason for existing is that printing zero is a worse
  // answer than admitting ignorance. "Not loaded yet" is ignorance too.
  const [occupancyState, setOccupancyState] = useState<"loading" | "ready" | "failed">("loading");
  const [sheds, setSheds] = useState<Shed[]>([]);
  // Live birds per shed, so "Capacity 5,000" can say how much of it is in use.
  // A capacity with no occupancy is a number nobody can act on: the decision
  // it exists for is where the next batch goes.
  const [birdsByShed, setBirdsByShed] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Shed | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [shedCode, setShedCode] = useState("");
  const [shedType, setShedType] = useState<"layer" | "brooder">("layer");
  const [capacity, setCapacity] = useState("");
  const [status, setStatus] = useState<"active" | "inactive" | "maintenance">("active");
  const [notes, setNotes] = useState("");

  const loadSheds = async () => {
    try {
      const data = await shedService.list();
      setSheds(data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  // Separate from loadSheds on purpose: the sheds themselves must render even
  // if this fails, and an occupancy we could not read is shown as unknown
  // rather than as an empty shed. "0 of 5,000" for a full shed is worse than
  // no figure at all.
  const loadOccupancy = async () => {
    try {
      const flocks = await flockService.list();
      const byShed: Record<number, number> = {};
      for (const f of flocks) {
        if (f.status !== "active") continue; // a culled flock occupies nothing
        byShed[f.shed_id] = (byShed[f.shed_id] ?? 0) + f.current_count;
      }
      setBirdsByShed(byShed);
      setOccupancyState("ready");
    } catch {
      setOccupancyState("failed");
    }
  };

  useEffect(() => {
    loadSheds();
    loadOccupancy();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setShedCode("");
    setShedType("layer");
    setCapacity("");
    setStatus("active");
    setNotes("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (shed: Shed) => {
    setEditing(shed);
    setShedCode(shed.shed_code);
    setShedType(shed.shed_type as "layer" | "brooder");
    setCapacity(shed.capacity?.toString() ?? "");
    setStatus(shed.status as "active" | "inactive" | "maintenance");
    setNotes(shed.notes ?? "");
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload: Partial<Shed> = {
      shed_code: shedCode,
      shed_type: shedType,
      capacity: capacity ? parseInt(capacity) : undefined,
      status,
      notes: notes || undefined,
    };

    try {
      if (editing) {
        await shedService.update(editing.id, payload);
      } else {
        await shedService.create(payload);
      }
      setModalOpen(false);
      await Promise.all([loadSheds(), loadOccupancy()]);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Styled ConfirmModal (not window.confirm) — holds the shed pending confirm.
  const [confirmDeactivate, setConfirmDeactivate] = useState<Shed | null>(null);

  const handleDeactivate = async () => {
    if (!confirmDeactivate) return;
    try {
      await shedService.deactivate(confirmDeactivate.id);
      setConfirmDeactivate(null);
      await Promise.all([loadSheds(), loadOccupancy()]);
    } catch (err) {
      setConfirmDeactivate(null);
      setError(handleApiError(err));
    }
  };

  return (
    <>
      <PageMeta title="Sheds | Poultry Book" description="Manage farm sheds" />
      <PageBreadcrumb pageTitle="Sheds" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            All Sheds
          </h3>
          {isOwner && (
            <Button size="sm" onClick={openCreate}>
              Add Shed
            </Button>
          )}
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : sheds.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No sheds found. Add your first shed.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Code
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Type
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Capacity
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    In use
                  </TableCell>
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </TableCell>
                  {isOwner && (
                    <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sheds.map((shed) => (
                  <TableRow
                    key={shed.id}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {shed.shed_code}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {shed.shed_type}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {shed.capacity?.toLocaleString() ?? "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      <ShedOccupancy
                        birds={birdsByShed[shed.id] ?? 0}
                        capacity={shed.capacity}
                        state={occupancyState}
                      />
                    </TableCell>
                    <TableCell className="px-5 py-3">
                      <Badge
                        size="sm"
                        color={statusColors[shed.status] ?? "light"}
                      >
                        {shed.status}
                      </Badge>
                    </TableCell>
                    {isOwner && (
                    <TableCell className="px-5 py-3 text-right">
                      <button
                        onClick={() => openEdit(shed)}
                        className="mr-3 text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      >
                        Edit
                      </button>
                      {shed.status !== "inactive" && (
                        <button
                          onClick={() => setConfirmDeactivate(shed)}
                          className="text-sm text-error-500 hover:text-error-600"
                        >
                          Deactivate
                        </button>
                      )}
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit Shed" : "Add Shed"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="shedCode">Shed Code *</Label>
            <Input
              id="shedCode"
              value={shedCode}
              onChange={(e) => setShedCode(e.target.value)}
              placeholder="e.g. L1, B2"
              required
              maxLength={20}
            />
          </div>

          <div>
            <Label htmlFor="shedType">Type *</Label>
            <select
              id="shedType"
              value={shedType}
              onChange={(e) => setShedType(e.target.value as "layer" | "brooder")}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="layer">Layer</option>
              <option value="brooder">Brooder</option>
            </select>
          </div>

          <div>
            <Label htmlFor="capacity">Capacity</Label>
            <Input
              id="capacity"
              type="number"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="e.g. 30000"
              min={1}
            />
          </div>

          <div>
            <Label htmlFor="status">Status *</Label>
            <select
              id="status"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as "active" | "inactive" | "maintenance")
              }
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !shedCode}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={confirmDeactivate !== null}
        title="Deactivate Shed"
        message={`Deactivate shed "${confirmDeactivate?.shed_code ?? ""}"? It will be hidden from active lists.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onClose={() => setConfirmDeactivate(null)}
      />
    </>
  );
}
